/**
 * Team administration schemas (the team-lead's member console — web).
 *
 * A team membership = one user + one role + custom permission overrides +
 * the languages they may work on within the team + a status. The team lead
 * (an actor holding `can_manage_team`) creates members with a password set
 * directly (no invite flow), edits their role/languages/status, can reset the
 * password, and removes them. Passwords are write-only (argon2-hashed); they are
 * never returned.
 */

import { z } from 'zod';
import { languoidCodeSchema } from './primitives.js';

export const membershipStatusSchema = z.enum(['ACTIVE', 'SUSPENDED']);
export type MembershipStatus = z.infer<typeof membershipStatusSchema>;

const passwordSchema = z.string().min(8, 'Password must be at least 8 characters.').max(200);

export const memberCreateSchema = z.object({
  displayName: z.string().min(1, 'A name is required.').max(120),
  email: z.string().email().transform((s) => s.trim().toLowerCase()),
  password: passwordSchema,
  roleId: z.string().min(1, 'A role is required.'),
  languoidCodes: z.array(languoidCodeSchema).default([]),
  customPermissions: z.array(z.string()).default([]),
  status: membershipStatusSchema.default('ACTIVE'),
});
export type MemberCreateInput = z.infer<typeof memberCreateSchema>;
export type MemberCreateRequest = z.input<typeof memberCreateSchema>;

/** Every field optional — a partial patch. `password` present = reset it. */
export const memberUpdateSchema = z.object({
  displayName: z.string().min(1).max(120).optional(),
  password: passwordSchema.optional(),
  roleId: z.string().min(1).optional(),
  languoidCodes: z.array(languoidCodeSchema).optional(),
  customPermissions: z.array(z.string()).optional(),
  status: membershipStatusSchema.optional(),
});
export type MemberUpdateInput = z.infer<typeof memberUpdateSchema>;

/** One team member, as the admin console sees them. No password is ever sent. */
export interface MemberSummary {
  userId: string;
  displayName: string | null;
  email: string;
  roleId: string | null;
  roleName: string | null;
  /** Effective permissions: role ∪ custom. */
  permissions: string[];
  customPermissions: string[];
  languoidCodes: string[];
  status: MembershipStatus;
  /** True when the role grants `can_manage_team` (i.e. a team lead). */
  isLead: boolean;
  approvedChallengesCount: number;
}

/** A role assignable to a member: the system roles + the team's custom roles. */
export interface RoleSummary {
  id: string;
  name: string;
  permissions: string[];
  isSystemRole: boolean;
}
